'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
import NavBar from '@/components/header/NavBar';
import { useLocale } from 'next-intl';
import { GlowingEffect } from '@/components/home-page/glowing-effect';
import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";
import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';

const ContactForms: React.FC = () => {

  return (<>
  {/* Fixed background — stays behind everything while page scrolls */}
  <div
    className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
    style={{ backgroundImage: `url('/hero/digital.avif')` }}
  >
   
<div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px] " />
  <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4 ">
   Technology & Digital
  </h1>
    
  </div>
 

  <NavBar />
   <ul className="grid grid-cols-1 bg-secondary grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 p-8 xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
        icon={<Box className="h-4 w-4 text-black dark:text-primary" />}
        title="SaaS & Software"
        description="We design and build scalable SaaS platforms and web applications, from MVP to full product, with clean architecture that grows with your user base."
      />
 
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
        icon={<Settings className="h-4 w-4 text-black dark:text-primary" />}
        title="Startups & Tech Founders"
        description="We help founders turn ideas into working products fast, with lean development, technical guidance, and a foundation built to scale as you grow."
      />
 
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
        icon={<Lock className="h-4 w-4 text-black dark:text-primary" />}
        title="Cybersecurity & Data"
        description="We build secure, reliable digital solutions for technology businesses, helping protect sensitive data, harden infrastructure, and earn user trust."
      />
 
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
        icon={<Sparkles className="h-4 w-4 text-black dark:text-primary" />}
        title="AI & Digital Products"
        description="We create high-performing digital products powered by modern technology, helping tech companies stand out, engage users, and turn innovation into growth."
      />
 
      <GridItem
        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
        icon={<Search className="h-4 w-4 text-black dark:text-primary" />}
        title="APIs & Integrations"
        description="We build robust APIs and third-party integrations that connect your systems, automate workflows, and create seamless experiences across your tech stack."
      />
    </ul>
<Why/>
<ContactForm/>

<FAQContainer
  title={"Technology &\nDigital FAQs"}
  description="Answers to the most common questions clients ask about our solutions for SaaS, startups, and technology-driven businesses."
  faq={[
    {
      question: "Can you help build my SaaS product from scratch?",
      answer:
        "Yes, we design and develop SaaS platforms from MVP through full-scale product, with architecture built to handle growth from day one.",
    },
    {
      question: "Do you work with early-stage startups?",
      answer:
        "Absolutely. We help founders move fast with lean development and technical guidance, turning ideas into working products without unnecessary overhead.",
    },
    {
      question: "How do you handle security for tech and data-driven products?",
      answer:
        "We build with security best practices from the start, including data protection, secure authentication, and infrastructure hardening for sensitive systems.",
    },
    {
      question: "Can you build or integrate APIs with our existing systems?",
      answer:
        "Yes, we build robust APIs and integrate with third-party services and existing systems to automate workflows and connect your tech stack.",
    },
    {
      question: "Do you have experience with AI-powered products?",
      answer:
        "Yes, we design and build digital products that incorporate modern AI capabilities, helping tech companies innovate and stand out.",
    },
    {
      question: "Can you scale an existing platform as our user base grows?",
      answer:
        "Yes, we audit and refactor existing architecture where needed and build with scalability in mind so your platform grows smoothly with demand.",
    },
  ]}
/>
<Footer/>
</>
  );
};

export default ContactForms;


interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
 
const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-[14rem] list-none  rounded-3xl ${area}`}>
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
     <GlowingEffect

  glow={true}
  disabled={false}
  spread={40}
  proximity={64}
  inactiveZone={0.01}
  borderWidth={2}
/>
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 ">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border text-primary border-white p-2">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 mt-8 text-xl  font-bold text-balance text-black md:text-3xl dark:text-primary">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black md:text-xl dark:text-white  font-light">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};